import { Injectable, Type } from '@angular/core';
import {
  ApiPaginatedResponse,
  ApiQuery,
  ApiResponse,
  BaseApiService,
  apiFilter,
  apiQueryToHttpParams,
} from '@kinet-open-network/client/core/api-client';
import { ItemPrice } from '@kinet-open-network/client/item-prices/data-access';
import { toModelArray } from '@kinet-open-network/client/shared/data-access/model';
import { StockMoveItem } from '@kinet-open-network/client/stock-moves/data-access';
import {
  IStockItemBalance,
  IStockItemProvisioningType,
  ProvisioningType,
} from '@kinet-open-network/shared/data-access/model';
import { plainToInstance } from 'class-transformer';
import { map, switchMap } from 'rxjs';
import { StockItemProvisioningType } from '../models/stock-item-provisioning-type.model';
import { StockItem } from '../models/stock-item.model';

@Injectable({
  providedIn: 'root',
})
export class StockItemsService extends BaseApiService<StockItem> {
  override model: Type<StockItem> = StockItem;

  override get endpoint(): string {
    return '/stock-items';
  }

  movements(stockItemId: string) {
    return this.http
      .get<
        ApiResponse<StockMoveItem[]>
      >(`${this.endpoint}/${stockItemId}/movements`)
      .pipe(map((response) => toModelArray(response.data, StockMoveItem)));
  }

  queryBalances(query?: ApiQuery, date?: Date) {
    const params = apiQueryToHttpParams({
      ...query,
      date: date ? date.toJSON() : undefined,
    });

    return this.http.get<ApiPaginatedResponse<IStockItemBalance>>(
      `${this.endpoint}/balances`,
      { params, context: this.subjectContext() },
    );
  }

  balances(stockItemId: string) {
    return this.http.get<ApiResponse<IStockItemBalance[]>>(
      `${this.endpoint}/${stockItemId}/balances`,
    );
  }

  getProvisioningTypes(stockItemId: string) {
    return this.http
      .get<
        ApiResponse<IStockItemProvisioningType[]>
      >(`${this.endpoint}/${stockItemId}/provisioning-types`)
      .pipe(
        map((response) =>
          plainToInstance(StockItemProvisioningType, response.data),
        ),
      );
  }

  setProvisioningTypes(
    stockItemId: string,
    provisioningTypes: IStockItemProvisioningType[],
  ) {
    return this.http
      .post<ApiResponse<IStockItemProvisioningType[]>>(
        `${this.endpoint}/${stockItemId}/provisioning-types`,
        {
          items: provisioningTypes,
        },
      )
      .pipe(
        map((response) =>
          plainToInstance(StockItemProvisioningType, response.data),
        ),
      );
  }

  getProvisioningDeviceType(
    stockItemId: string,
    provisioningType: ProvisioningType,
  ) {
    return this.http
      .get<
        ApiResponse<IStockItemProvisioningType | null>
      >(`${this.endpoint}/${stockItemId}/provisioning-types/${provisioningType}`)
      .pipe(map((response) => response.data?.provisioningValue || null));
  }

  getProvisioningDeviceTypeBySn(
    sn: string,
    provisioningType: ProvisioningType,
  ) {
    return this.getBySn(sn).pipe(
      switchMap((stockItem) => {
        if (!stockItem) {
          return [null];
        }

        return this.getProvisioningDeviceType(stockItem.id, provisioningType);
      }),
    );
  }

  getBySn(sn: string) {
    return this.query({
      filters: apiFilter<StockItem>().where('sn', sn),
    }).pipe(
      map((response) => (response.data.length > 0 ? response.data[0] : null)),
    );
  }

  getPrices(stockItemId: string) {
    return this.http
      .get<ItemPrice[]>(`${this.endpoint}/${stockItemId}/pricelist`)
      .pipe(map((data) => plainToInstance(ItemPrice, data)));
  }
}
